import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_SITE } from '../siteDefaults';
import { resolveImpactStats } from '../siteImpactStats';
import { fetchSite, getToken, patchImpactStats } from './api';
import { input, label } from './adminUi';

const ICON_OPTIONS = [
  { value: 'farmers', label: 'Farmers reach' },
  { value: 'district', label: 'District / truck' },
  { value: 'distributors', label: 'Distributors' },
  { value: 'retailers', label: 'Retailers' },
  { value: 'projects', label: 'Projects' },
  { value: 'contractFarmers', label: 'Contract farmers' },
  { value: 'export', label: 'Export' },
  { value: 'employees', label: 'Employees' },
];

function emptyRow() {
  return { value: '', label: '', icon: 'farmers' };
}

export default function ImpactStatsDashboard() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSite()
      .then((site) => setRows(resolveImpactStats(site)))
      .catch(() => setRows(resolveImpactStats(DEFAULT_SITE)));
  }, [navigate]);

  function setRow(i, field, value) {
    setRows((r) => r.map((row, j) => (j === i ? { ...row, [field]: value } : row)));
  }

  function addRow() {
    setRows((r) => [...r, emptyRow()]);
  }

  function removeRow(i) {
    setRows((r) => r.filter((_, j) => j !== i));
  }

  async function save() {
    setMsg('');
    setErr('');
    setSaving(true);
    try {
      const { impactStats } = await patchImpactStats(rows);
      setRows(resolveImpactStats({ impactStats: impactStats || rows }));
      setMsg('Saved.');
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <h1 className="text-lg font-bold text-neutral-900">Impact stats</h1>
          <p className="text-sm text-neutral-600">White card below the hero (numbers + labels)</p>
        </div>
        <button
          type="button"
          disabled={saving}
          onClick={save}
          className="rounded-lg bg-brand-olive px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-8">
        {msg ? (
          <p className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
            {msg}
          </p>
        ) : null}
        {err ? (
          <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</p>
        ) : null}

        <div className="w-full">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-neutral-600">Each row appears in the grid on the home page.</p>
            <button
              type="button"
              onClick={addRow}
              className="rounded-lg border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-800"
            >
              Add row
            </button>
          </div>
          <div className="flex flex-col gap-4">
            {rows.map((row, i) => (
              <div
                key={i}
                className="grid gap-3 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end"
              >
                <label className={label}>
                  Value
                  <input className={input} value={row.value} onChange={(e) => setRow(i, 'value', e.target.value)} />
                </label>
                <label className={label}>
                  Label
                  <input className={input} value={row.label} onChange={(e) => setRow(i, 'label', e.target.value)} />
                </label>
                <label className={label}>
                  Icon
                  <select
                    className={input}
                    value={row.icon}
                    onChange={(e) => setRow(i, 'icon', e.target.value)}
                  >
                    {ICON_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="h-10 rounded-lg border border-red-200 text-sm font-medium text-red-700"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
